import { AccessControl } from "@components/AccessControl/AccessControl";
import { Body } from "@components/Body/Body";
import { Maintenance } from "@components/Maintenance/Maintenance";
import { SEO } from "@components/SEO/SEO";
import { AuthDataProvider } from "@contexts";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Heading } from "@jmc/solid-design-system/src/components/molecules/Heading/Heading";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { ValidAuthResults } from "@redux/modules/authValid";
import {
    AccessLevel,
    CMS_MEDICAL_CONTENT,
    CMS_PROMOTIONAL_CONTENT,
    CMSEventProps,
    CMSFreeFormPageSiteReference,
    CMSLegalDocuments,
    CMSPage,
    CMSProductProps,
    CMSSpecialtyProps,
    PageContext,
} from "@types";
import { graphql } from "gatsby";
import isEqual from "lodash/isEqual";
import React, { Fragment } from "react";
import { useTranslation, WithTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import yn from "yn";

import style from "./maintenance-template.module.scss";

interface PropTypes extends WithTranslation {
    data: {
        page: CMSPage;
        legalDocuments: CMSLegalDocuments;
    };
    pageContext: PageContext;
    authenticated?: boolean;
    authData?: ValidAuthResults;
}

export const MaintenancePage = (props: PropTypes): JSX.Element => {
    const authData = useSelector(
        ({ authValid }: { authValid: { data: ValidAuthResults } }) => authValid?.data,
        isEqual,
    );
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();
    const { site_reference } = props.data.page;
    const siteReference: CMSFreeFormPageSiteReference = site_reference && site_reference.length && site_reference[0];
    const relatedTo: CMSProductProps | CMSSpecialtyProps | CMSEventProps = siteReference && siteReference.relation[0];
    const isProductPage = (): boolean => {
        return relatedTo?.__typename === "Contentstack_product";
    };
    const siteMaintenance = siteReference?.maintenance_mode?.temporary_maintenance_mode ?? false;
    const pageMaintenance = props.data.page.maintenance_mode?.temporary_maintenance_mode ?? false;
    const checkIfMaintenance = (): boolean => {
        return pageMaintenance || siteMaintenance;
    };

    const getMaintenanceFragment = (): CMSFragmentProps => {
        if (checkIfMaintenance()) {
            const maintenanceFragment =
                pageMaintenance && props.data.page.maintenance_mode?.maintenance_fragment?.length
                    ? props.data.page.maintenance_mode?.maintenance_fragment
                    : siteReference.maintenance_mode?.maintenance_fragment;
            return maintenanceFragment?.length ? maintenanceFragment[0].body : null;
        }
        return null;
    };

    const {
        data: {
            page: {
                uid,
                title,
                page_title,
                seo_settings,
                content,
                regulatory_status,
                access_control: { access_level },
            },
        },
        pageContext,
    } = props;

    const { gatedUrl, publicContent, locale } = pageContext;

    const metaTitleSuffix = siteReference?.meta_fields?.title_suffix
        ? " | " + siteReference?.meta_fields?.title_suffix
        : "";

    const metaTitle = seo_settings?.meta_title
        ? seo_settings?.meta_title + metaTitleSuffix
        : page_title + metaTitleSuffix;

    const METADATA = {
        title: metaTitle || title,
        description:
            seo_settings.page_description || siteReference?.meta_fields?.site_description || "default description",
        canonical: seo_settings?.canonical_tag?.replace(/\/+$/, ""),
        hreflangs: seo_settings?.hreflang_associated_url,
    };

    const commercial = regulatory_status.promotional_or_medical === CMS_PROMOTIONAL_CONTENT;

    const headerBackground = "primary";

    const userProfiles = siteReference?.access_control?.user_profiles;

    const l3 = commercial ? access_level : AccessLevel.level3;

    const hreflangTags =
        pageContext?.availableLocales?.length > 1
            ? {
                  locale: pageContext?.availableLocales,
                  url: pageContext?.url,
              }
            : null;

    const hasPaywall = (): boolean => {
        if (!yn(process.env.GATSBY_PAYWALL_ENABLED)) return false;
        if (seo_settings.paywall === "inherit") return siteReference?.meta_fields?.paywall;
        if (seo_settings.paywall === "on") return true;
        return false;
    };

    const getTitle = (): string => {
        if (pageMaintenance && !siteMaintenance) return t("page under maintenance", { ns: "common" });
        if (siteMaintenance && !pageMaintenance) return t("site under maintenance", { ns: "common" });
        if (siteMaintenance && pageMaintenance) return t("site under maintenance", { ns: "common" });
    };

    const Wrapper = (): JSX.Element => {
        return getMaintenanceFragment() ? (
            <>
                {jnjFullBranded && (
                    <div className={style.maintenance}>
                        <Typography variant="h1" size="4xl" color="text-01" className={style.title}>
                            {getTitle()}
                        </Typography>
                    </div>
                )}
                <Body
                    section={getMaintenanceFragment()}
                    placement={page_title}
                    locale={locale}
                    relatedProduct={isProductPage ? (relatedTo as CMSProductProps) : undefined}
                    marginXl
                    footnotes={null}
                    smpcs={[]}
                    addContentContainerWidth={true}
                />
            </>
        ) : (
            <Maintenance title={jnjFullBranded && getTitle()} />
        );
    };

    return (
        <AuthDataProvider authData={authData} accessLevel={access_level}>
            <SEO
                title={getTitle()}
                description={METADATA.description}
                canonical={METADATA.canonical}
                hreflangs={METADATA.hreflangs}
                availableLocale={hreflangTags}
                level={l3}
                index={seo_settings?.indexing_and_sitemap || "default"}
                context={pageContext}
                paywall={hasPaywall()}
                currentPageUrl={pageContext?.url}
                open_graph={seo_settings?.open_graph}
                twitter={seo_settings.twitter}
                uid={uid}
                contentType="page"
                maintenancePage={checkIfMaintenance()}
            />
            <Fragment>
                {!jnjFullBranded && (
                    <header data-test-id="maintenance-main-header" style={{ marginBottom: "2rem" }}>
                        <Heading
                            title={getTitle()}
                            height={"Small"}
                            backgroundColor={headerBackground}
                            marginXl={false}
                            info={null}
                        />
                    </header>
                )}
                <AccessControl
                    gatedUrl={gatedUrl}
                    publicContent={publicContent}
                    content={content}
                    // Medical content should always be level3
                    level={commercial ? access_level : AccessLevel.level3}
                    combinedMedical={regulatory_status?.promotional_or_medical === CMS_MEDICAL_CONTENT}
                    allowedUserProfiles={userProfiles}
                    paywall={hasPaywall()}
                >
                    <Wrapper />
                </AccessControl>
            </Fragment>
        </AuthDataProvider>
    );
};

export default MaintenancePage;

export const query = graphql`
    query ($id: String!) {
        page: contentstackPage(id: { eq: $id }) {
            title
            uid
            title
            page_title
            table_of_content
            url
            updated_at
            ...bodyFields
            seo_settings {
                meta_title
                page_description
                canonical_tag
                hreflang_associated_url
                indexing_and_sitemap
                paywall
                twitter {
                    title
                    description
                    image {
                        url
                    }
                }
                open_graph {
                    title
                    description
                    image {
                        url
                    }
                }
            }
            regulatory_status {
                promotional_or_medical
            }
            access_control {
                access_level
            }
            header_visual {
                header_visual_source
                header_image_size
                header_image_from_file {
                    url
                    gatsbyImageData(layout: FULL_WIDTH)
                }
                image_focus {
                    image_focus
                }
                select_hero {
                    ...heroFields
                }
                select_carousel {
                    ...carouselFields
                }
                variant
                background_color
            }
            maintenance_mode {
                temporary_maintenance_mode
                maintenance_fragment {
                    ...fragmentFields
                }
            }
            site_reference {
                maintenance_mode {
                    temporary_maintenance_mode
                    maintenance_fragment {
                        ...fragmentFields
                    }
                }
                meta_fields {
                    title_suffix
                    site_description
                    paywall
                }
                ae_pi_disclaimer
                branding {
                    header_visual {
                        url
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                    logo {
                        url
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                    image_focus {
                        image_focus
                    }
                }
                startpage {
                    uid
                    url
                    access_control {
                        access_level
                    }
                }
                relation {
                    __typename
                    ... on Contentstack_product {
                        uid
                        productTitle: title
                        active_ingredient
                        pharmacovigiliance
                        productLogo: logo {
                            url
                            gatsbyImageData(layout: FULL_WIDTH)
                        }
                    }
                    ... on Contentstack_specialty {
                        specialtyTitle: title
                        specialtyLogo: logo {
                            url
                            gatsbyImageData(layout: FULL_WIDTH)
                        }
                    }
                    ... on Contentstack_event {
                        eventTitle: title
                        visual {
                            url
                            gatsbyImageData(layout: FULL_WIDTH)
                        }
                    }
                }
                site_menu {
                    submenu {
                        menu_label
                        link {
                            internal_page_link {
                                internal_link_anchor_id
                                internal_link_parameters
                                visibility
                                link {
                                    ... on Contentstack_page {
                                        page_url: url
                                        access_control {
                                            access_level
                                        }
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                    }
                                    ... on Contentstack_request_materials {
                                        request_materials_uid: uid
                                    }
                                    ... on Contentstack_media_center {
                                        media_center_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_service_overview {
                                        service_overview_uid: uid
                                    }
                                    ... on Contentstack_news_center {
                                        news_center_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_ask_the_experts {
                                        ask_the_experts_uid: uid
                                    }
                                    ... on Contentstack_drug_to_drug {
                                        drug_to_drug_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_learning_center {
                                        lms_uid: uid
                                    }
                                    ... on Contentstack_transfer_of_value {
                                        transfer_of_value_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_event_center {
                                        event_center_uid: uid
                                    }
                                    ... on Contentstack_external_resource {
                                        external_resource_url: url {
                                            href
                                        }
                                    }
                                    ... on Contentstack_material {
                                        material_id: uid
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_article {
                                        article_id: uid
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_video {
                                        video_id
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_audio {
                                        audio_id
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_event {
                                        event_id: iconnect_event_number
                                    }
                                    ... on Contentstack_product_glossary {
                                        product_glossary: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_myjmc {
                                        myjmc_id: id
                                    }
                                    ... on Contentstack_information_on_demand {
                                        iod_id: uid
                                    }
                                    ... on Contentstack_course {
                                        course_id: moodle_course_number
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                    }
                                    ... on Contentstack_podcast {
                                        podcast_title: title
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_tcp_home {
                                        page_url: url
                                    }
                                    ... on Contentstack_scientific_publications {
                                        scientific_publications_uid: uid
                                    }
                                    ... on Contentstack_scientific_publication {
                                        sp_detail_id: uid
                                    }
                                    ... on Contentstack_pubmed {
                                        pubmed_id: id
                                        access_control {
                                            access_level
                                        }
                                    }
                                }
                            }
                            external_link {
                                link {
                                    href
                                }
                            }
                            file_asset {
                                file_asset_upload {
                                    url: transformedUrl
                                }
                                visibility
                            }
                        }
                        menu_item {
                            internal_page_link {
                                menu_label
                                anchor_id
                                link {
                                    ... on Contentstack_page {
                                        page_url: url
                                        access_control {
                                            access_level
                                        }
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                    }
                                    ... on Contentstack_request_materials {
                                        request_materials_uid: uid
                                    }
                                    ... on Contentstack_media_center {
                                        media_center_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_service_overview {
                                        service_overview_uid: uid
                                    }
                                    ... on Contentstack_news_center {
                                        news_center_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_podcast_center {
                                        podcast_center_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_specialties {
                                        specialties_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_transfer_of_value {
                                        transfer_of_value_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_podcast {
                                        podcast_title: title
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_tcp_home {
                                        page_url: url
                                    }
                                    ... on Contentstack_event_center {
                                        event_center_uid: uid
                                    }
                                    ... on Contentstack_external_resource {
                                        external_resource_url: url {
                                            href
                                        }
                                    }
                                    ... on Contentstack_material {
                                        material_id: uid
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_article {
                                        article_id: uid
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_video {
                                        video_id
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_audio {
                                        audio_id
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_event {
                                        event_id: iconnect_event_number
                                    }
                                    ... on Contentstack_pubmed {
                                        pubmed_id: id
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_product_glossary {
                                        product_glossary: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_myjmc {
                                        myjmc_id: id
                                    }
                                    ... on Contentstack_information_on_demand {
                                        iod_id: uid
                                    }
                                    ... on Contentstack_ask_the_experts {
                                        ask_the_experts_uid: uid
                                    }
                                    ... on Contentstack_drug_to_drug {
                                        drug_to_drug_uid: uid
                                        access_control {
                                            access_level
                                        }
                                    }
                                    ... on Contentstack_learning_center {
                                        lms_uid: uid
                                    }
                                    ... on Contentstack_course {
                                        course_id: moodle_course_number
                                        regulatory_status {
                                            promotional_or_medical
                                        }
                                    }
                                    ... on Contentstack_scientific_publication {
                                        sp_detail_id: uid
                                    }
                                    ... on Contentstack_scientific_publications {
                                        scientific_publications_uid: uid
                                    }
                                }
                            }
                            external_link {
                                link {
                                    href
                                    title
                                }
                            }
                            file_asset {
                                menu_label
                                file_asset_upload {
                                    url: transformedUrl
                                }
                            }
                            submenu {
                                menu_label
                                link {
                                    internal_page_link {
                                        internal_link_anchor_id
                                        internal_link_parameters
                                        visibility
                                        link {
                                            ... on Contentstack_page {
                                                page_url: url
                                                access_control {
                                                    access_level
                                                }
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                            }
                                            ... on Contentstack_request_materials {
                                                request_materials_uid: uid
                                            }
                                            ... on Contentstack_media_center {
                                                media_center_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_service_overview {
                                                service_overview_uid: uid
                                            }
                                            ... on Contentstack_news_center {
                                                news_center_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_ask_the_experts {
                                                ask_the_experts_uid: uid
                                            }
                                            ... on Contentstack_drug_to_drug {
                                                drug_to_drug_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_learning_center {
                                                lms_uid: uid
                                            }
                                            ... on Contentstack_transfer_of_value {
                                                transfer_of_value_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_event_center {
                                                event_center_uid: uid
                                            }
                                            ... on Contentstack_external_resource {
                                                external_resource_url: url {
                                                    href
                                                }
                                            }
                                            ... on Contentstack_material {
                                                material_id: uid
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_article {
                                                article_id: uid
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_video {
                                                video_id
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_audio {
                                                audio_id
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_event {
                                                event_id: iconnect_event_number
                                            }
                                            ... on Contentstack_pubmed {
                                                pubmed_id: id
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_product_glossary {
                                                product_glossary: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_myjmc {
                                                myjmc_id: id
                                            }
                                            ... on Contentstack_information_on_demand {
                                                iod_id: uid
                                            }
                                            ... on Contentstack_course {
                                                course_id: moodle_course_number
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                            }
                                            ... on Contentstack_scientific_publications {
                                                scientific_publications_uid: uid
                                            }
                                            ... on Contentstack_scientific_publication {
                                                sp_detail_id: uid
                                            }
                                            ... on Contentstack_pubmed {
                                                pubmed_id: id
                                                access_control {
                                                    access_level
                                                }
                                            }
                                        }
                                    }
                                    external_link {
                                        link {
                                            href
                                        }
                                    }
                                    file_asset {
                                        file_asset_upload {
                                            url: transformedUrl
                                        }
                                        visibility
                                    }
                                }
                                menu_item {
                                    internal_page_link {
                                        menu_label
                                        anchor_id
                                        link {
                                            ... on Contentstack_page {
                                                page_url: url
                                                access_control {
                                                    access_level
                                                }
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                            }
                                            ... on Contentstack_request_materials {
                                                request_materials_uid: uid
                                            }
                                            ... on Contentstack_media_center {
                                                media_center_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_service_overview {
                                                service_overview_uid: uid
                                            }
                                            ... on Contentstack_news_center {
                                                news_center_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_podcast_center {
                                                podcast_center_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_specialties {
                                                specialties_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_transfer_of_value {
                                                transfer_of_value_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_event_center {
                                                event_center_uid: uid
                                            }
                                            ... on Contentstack_external_resource {
                                                external_resource_url: url {
                                                    href
                                                }
                                            }
                                            ... on Contentstack_material {
                                                material_id: uid
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_article {
                                                article_id: uid
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_video {
                                                video_id
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_audio {
                                                audio_id
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_event {
                                                event_id: iconnect_event_number
                                            }
                                            ... on Contentstack_pubmed {
                                                pubmed_id: id
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_product_glossary {
                                                product_glossary: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_myjmc {
                                                myjmc_id: id
                                            }
                                            ... on Contentstack_information_on_demand {
                                                iod_id: uid
                                            }
                                            ... on Contentstack_ask_the_experts {
                                                ask_the_experts_uid: uid
                                            }
                                            ... on Contentstack_drug_to_drug {
                                                drug_to_drug_uid: uid
                                                access_control {
                                                    access_level
                                                }
                                            }
                                            ... on Contentstack_learning_center {
                                                lms_uid: uid
                                            }
                                            ... on Contentstack_course {
                                                course_id: moodle_course_number
                                                regulatory_status {
                                                    promotional_or_medical
                                                }
                                            }
                                            ... on Contentstack_scientific_publication {
                                                sp_detail_id: uid
                                            }
                                            ... on Contentstack_scientific_publications {
                                                scientific_publications_uid: uid
                                            }
                                        }
                                    }
                                    external_link {
                                        link {
                                            href
                                            title
                                        }
                                    }
                                    file_asset {
                                        menu_label
                                        file_asset_upload {
                                            url: transformedUrl
                                        }
                                    }
                                }
                            }
                        }
                    }
                    internal_page_link {
                        menu_label
                        anchor_id
                        link {
                            ... on Contentstack_page {
                                page_url: url
                                access_control {
                                    access_level
                                }
                                regulatory_status {
                                    promotional_or_medical
                                }
                            }
                            ... on Contentstack_request_materials {
                                request_materials_uid: uid
                            }
                            ... on Contentstack_media_center {
                                media_center_uid: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_service_overview {
                                service_overview_uid: uid
                            }
                            ... on Contentstack_news_center {
                                news_center_uid: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_podcast_center {
                                podcast_center_uid: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_specialties {
                                specialties_uid: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_transfer_of_value {
                                transfer_of_value_uid: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_event_center {
                                event_center_uid: uid
                            }
                            ... on Contentstack_external_resource {
                                external_resource_url: url {
                                    href
                                }
                            }
                            ... on Contentstack_material {
                                material_id: uid
                                regulatory_status {
                                    promotional_or_medical
                                }
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_article {
                                article_id: uid
                                regulatory_status {
                                    promotional_or_medical
                                }
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_video {
                                video_id
                                regulatory_status {
                                    promotional_or_medical
                                }
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_audio {
                                audio_id
                                regulatory_status {
                                    promotional_or_medical
                                }
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_event {
                                event_id: iconnect_event_number
                            }
                            ... on Contentstack_pubmed {
                                pubmed_id: id
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_product_glossary {
                                product_glossary: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_myjmc {
                                myjmc_id: id
                            }
                            ... on Contentstack_information_on_demand {
                                iod_id: uid
                            }
                            ... on Contentstack_ask_the_experts {
                                ask_the_experts_uid: uid
                            }
                            ... on Contentstack_drug_to_drug {
                                drug_to_drug_uid: uid
                                access_control {
                                    access_level
                                }
                            }
                            ... on Contentstack_learning_center {
                                lms_uid: uid
                            }
                            ... on Contentstack_course {
                                course_id: moodle_course_number
                                regulatory_status {
                                    promotional_or_medical
                                }
                            }
                            ... on Contentstack_scientific_publication {
                                sp_detail_id: uid
                            }
                            ... on Contentstack_scientific_publications {
                                scientific_publications_uid: uid
                            }
                        }
                    }
                    external_link {
                        link {
                            href
                            title
                        }
                    }
                    file_asset {
                        menu_label
                        file_asset_upload {
                            url: transformedUrl
                        }
                    }
                }
                smpc_reference {
                    uid
                    title
                    display_title
                    smpc_source
                    generate_detail_page
                    external_link
                    file_asset {
                        url: transformedUrl
                    }
                    page_reference {
                        ... on Contentstack_page {
                            page_url: url
                            regulatory_status {
                                promotional_or_medical
                            }
                        }
                        ... on Contentstack_material {
                            material_id: uid
                            regulatory_status {
                                promotional_or_medical
                            }
                        }
                    }
                }
            }
            fields {
                footnotes {
                    footnotes {
                        index
                        text
                    }
                    text_without_footnotes
                    text
                    mapped_text
                }
            }
            smpc_reference {
                uid
                title
                display_title
                smpc_source
                generate_detail_page
                external_link
                file_asset {
                    url: transformedUrl
                }
                page_reference {
                    ... on Contentstack_page {
                        page_url: url
                        regulatory_status {
                            promotional_or_medical
                        }
                    }
                    ... on Contentstack_material {
                        material_id: uid
                        regulatory_status {
                            promotional_or_medical
                        }
                    }
                }
            }
        }
    }
`;
